<script setup>
import { useEmployerStore } from '@/shared/stores/EmployerStore'
import { useRenewalEmployerStore } from '@/shared/stores/RenewalEmployerStore'
import { storeToRefs } from 'pinia'
import { inject, defineProps } from 'vue'
import { PLAN_YEAR_EVENTS } from '@/shared/utils/planYearEvents'

// Get data from stores
const employerStore = useEmployerStore()
const renewalStore = useRenewalEmployerStore()
const { actualPlanYear, planYearsOfActualEmployer, planStartDateCopy } = storeToRefs(employerStore)
const { isRenewalDisabled } = storeToRefs(renewalStore)

// Get emitter
const emitter = inject('emitter')

// Enable short date formatting (month and day only)
const props = defineProps({ shortFormat: { type: Boolean, default: false } })

// Format plan year date for display
const formatPlanYearDate = (date) => {
  if (!date) return 'Plan Year'
  const d = new Date(date)
  if (props.shortFormat) {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return `Plan Year ${monthNames[d.getMonth()]} ${d.getDate()}`
  }
  const formattedDate = d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
  return `Plan Year ${formattedDate}`
}

// Handler for plan year change
const handleChangePlanYear = (planYearId) => {
  emitter.emit(PLAN_YEAR_EVENTS.CHANGE_PLAN_YEAR, { planYearId })
}

// Handler for opening renewal modal
const handleOpenRenewEmployerModal = () => {
  if (!isRenewalDisabled.value) {
    emitter.emit(PLAN_YEAR_EVENTS.OPEN_RENEW_EMPLOYER_MODAL)
  }
}  
</script>

<template>
  <div class="dropdown">
    <button 
      type="button" 
      class="btn btn-outline-secondary dropdown-toggle d-flex align-items-center btn-plan-year px-2.5 py-1.5" 
      id="planYearDropdown" 
      data-bs-toggle="dropdown" 
      aria-expanded="false"
    >
      <span class="text-14 text-primary"><med>{{ formatPlanYearDate(planStartDateCopy || actualPlanYear?.plan_start_date) }}</med></span>
    </button>
    <ul class="dropdown-menu py-0" aria-labelledby="planYearDropdown" style="min-width: 250px;">
      <li 
        v-for="planYear in planYearsOfActualEmployer || []" 
        :key="planYear.id" 
        class="dropdown-item hand-cursor" 
        :class="{'bg-primary text-white': planYear.id === actualPlanYear?.id}"
        @click="handleChangePlanYear(planYear.id)" 
        @keydown.enter="$event.target.click()" 
        tabindex="0"
      >
        <span>{{ formatPlanYearDate(planYear.plan_start_date) }}</span>
      </li>
      <li 
        class="hand-cursor" 
        :class="{'text-muted ps-3 pt-1': isRenewalDisabled, 'dropdown-item': !isRenewalDisabled}" 
        @click="handleOpenRenewEmployerModal()"
      >
        <span>+ Renew This Employer</span>
      </li>
    </ul>
  </div>
</template>

<style scoped lang="scss">
.btn-plan-year {
  border-color: var(--bs-primary);
  color: var(--bs-primary);
  border-radius: 0.25rem;
}
.btn-plan-year:hover {
  border-color: var(--bs-primary);
  color: var(--bs-primary);
  background-color: transparent;
}
.btn-plan-year:focus-visible {
  border-color: var(--bs-primary);
  color: var(--bs-primary);
  background-color: transparent;
}
.dropdown-item {
  color: var(--bs-primary);
}
.dropdown-item:hover,
.dropdown-item:focus {
  color: var(--bs-white);
  background-color: var(--bs-primary);
}
.dropdown {
  z-index: 1050;
}
</style>